import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

export interface DropdownItem {
  label: string;
  onSelect: () => void;
  icon?: ReactNode;
  destructive?: boolean;
  disabled?: boolean;
  /** Tooltip shown on hover — used to explain why a disabled action is unavailable. */
  title?: string;
}

export interface DropdownProps {
  trigger: (props: { open: boolean; toggle: () => void; id: string }) => ReactNode;
  items: DropdownItem[];
  align?: "start" | "end";
}

/** Accessible menu dropdown: outside-click + ESC close, arrow-key navigation.
 *
 * The menu renders via createPortal to document.body with fixed
 * positioning derived from the trigger's bounding rect, so it escapes
 * any ancestor's overflow:hidden / transform / contain. Library cards
 * have rounded corners + overflow:hidden on the article wrapper to
 * clip the cover-art thumbnail; without the portal the dropdown was
 * being clipped at the card edge.
 */
export function Dropdown({ trigger, items, align = "end" }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useRef(`menu-${Math.random().toString(36).slice(2)}`).current;
  const MENU_MIN_W = 180;
  const MENU_GAP = 6;

  // Recompute menu coords whenever it opens (and on scroll/resize while
  // open, so a scrolling library doesn't leave the menu behind).
  useLayoutEffect(() => {
    if (!open) return;
    const compute = () => {
      const trig = rootRef.current?.firstElementChild as HTMLElement | null;
      if (!trig) return;
      const rect = trig.getBoundingClientRect();
      const menuW = menuRef.current?.offsetWidth ?? MENU_MIN_W;
      const left =
        align === "end" ? Math.max(8, rect.right - menuW) : Math.min(window.innerWidth - menuW - 8, rect.left);
      const top = rect.bottom + MENU_GAP;
      setCoords({ top, left });
    };
    compute();
    window.addEventListener("scroll", compute, true);
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute, true);
      window.removeEventListener("resize", compute);
    };
  }, [open, align]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      // Treat both the trigger and the portaled menu as "inside" —
      // a click on a menu item shouldn't close before the onClick
      // handler fires, and a click on the trigger button toggles
      // open/close itself.
      if (rootRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((a) => Math.min(a + 1, items.length - 1));
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((a) => Math.max(a - 1, 0));
      }
      if (e.key === "Enter" && active >= 0) {
        const item = items[active];
        if (item && !item.disabled) {
          item.onSelect();
          setOpen(false);
        }
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, active, items]);

  return (
    <div ref={rootRef} style={{ position: "relative", display: "inline-block" }}>
      {trigger({
        open,
        toggle: () => {
          setOpen((o) => !o);
          setActive(-1);
        },
        id: menuId,
      })}
      {open && coords
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              id={menuId}
              style={{
                position: "fixed",
                top: coords.top,
                left: coords.left,
                minWidth: MENU_MIN_W,
                background: "var(--bg-elev)",
                border: "1px solid var(--border)",
                borderRadius: 8,
                boxShadow: "0 24px 64px -24px rgba(0,0,0,.6)",
                padding: 6,
                zIndex: 900,
                animation: "vocFadeUp 150ms cubic-bezier(0.16,1,0.3,1)",
              }}
            >
              {items.map((item, i) => (
                <button
                  key={item.label}
                  role="menuitem"
                  type="button"
                  disabled={item.disabled}
                  title={item.title}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => {
                    if (item.disabled) return;
                    item.onSelect();
                    setOpen(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    width: "100%",
                    padding: "8px 10px",
                    borderRadius: 6,
                    border: "none",
                    background: i === active ? "var(--bg-hover)" : "transparent",
                    color: item.destructive ? "#FF2D1F" : "var(--fg)",
                    fontSize: 13,
                    textAlign: "left",
                    cursor: item.disabled ? "not-allowed" : "pointer",
                    opacity: item.disabled ? 0.5 : 1,
                  }}
                >
                  {item.icon}
                  {item.label}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
